import { MARKER_DIAMETER } from "@/lib/timeline/marker/MarkerMetrics/metrics";

export interface Point {
  x: number;
  y: number;
}

/** From a portrait's center to the bottom right of its rim, along each axis. */
const CORNER_OFFSET = (MARKER_DIAMETER / 2) * Math.SQRT1_2;
/** A little further down, so more of the face stays clear. */
const LOWER_BY = 14;
/** Who counts as being in the holders' group, rather than on another rail. */
const SAME_GROUP = MARKER_DIAMETER * 5;
/** Beside two people stacked one above the other, whose names fill the space between them. */
const BESIDE_A_PAIR = MARKER_DIAMETER * 0.9;
/** A small circle has a name at its top reaching the middle, so the open space is lower. */
const BELOW_A_NAME = MARKER_DIAMETER * 0.3;
const SMALL_CIRCLE = 4;

const average = (points: readonly Point[]): Point => ({
  x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
  y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
});

/**
 * With one person, an item sits on the bottom right of their portrait. Shared by several,
 * it sits in the open space of their group, clear of faces and names.
 */
export function keyItemCenter(holders: readonly Point[], cast: readonly Point[]): Point | null {
  const [holder] = holders;
  if (holder === undefined) {
    return null;
  }
  if (holders.length === 1) {
    return { x: holder.x + CORNER_OFFSET, y: holder.y + CORNER_OFFSET + LOWER_BY };
  }
  const near = average(holders);
  const group = cast.filter((point) => (
    Math.hypot(point.x - near.x, point.y - near.y) < SAME_GROUP
  ));
  const everyone = group.length < holders.length ? holders : group;
  const middle = average(everyone);
  if (everyone.length === 2) {
    return { x: middle.x + BESIDE_A_PAIR, y: middle.y };
  }
  return everyone.length <= SMALL_CIRCLE ? { x: middle.x, y: middle.y + BELOW_A_NAME } : middle;
}
